import React, { useEffect, useState } from 'react';
import { priceTooltipText } from '../../utils/number';
import PriceCostBasisHint from '../../components/PriceCostBasisHint';

interface ListingForEdit {
 listing_hash: string;
 side: 'seeking_advertiser' | 'seeking_media' | 'seeking_member';
 title: string;
 description?: string | null;
 period_start: string;
 period_end: string;
 price_per_impression: string | number;
 industry_ksic_major?: string | null;
 region_sido?: string | null;
 region_gugun?: string | null;
 daily_budget_cap?: string | number | null;
 guaranteed_impressions?: number | null;
}

interface Props {
 moFamilyHash: string;
 onClose: () => void;
 onCreated?: () => void;
 editing?: ListingForEdit | null;
 /** 새 등록 시 처음 선택될 유형(마켓 탭에서 열면 그 탭의 유형). editing 이 있으면 무시. */
 initialSide?: 'seeking_advertiser' | 'seeking_media' | 'seeking_member';
}

interface SlotVideo {
 file_hash: string;
 filename: string;
 thumbnail_path?: string;
 duration?: number;
}

const MarketRegisterModal: React.FC<Props> = ({ moFamilyHash, onClose, onCreated, editing, initialSide }) => {
 const [side, setSide] = useState<'seeking_advertiser' | 'seeking_media' | 'seeking_member'>(
  editing?.side ?? initialSide ?? 'seeking_advertiser'
 );
 const [title, setTitle] = useState(editing?.title ?? '');
 const [description, setDescription] = useState(editing?.description ?? '');
 const [periodStart, setPeriodStart] = useState(editing ? String(editing.period_start).slice(0, 10) : '');
 const [periodEnd, setPeriodEnd] = useState(editing ? String(editing.period_end).slice(0, 10) : '');
 const [price, setPrice] = useState(editing ? String(editing.price_per_impression) : '1');
 const [dailyBudgetCap, setDailyBudgetCap] = useState(
  editing?.daily_budget_cap != null ? String(editing.daily_budget_cap) : ''
 );
 const [guaranteedImpressions, setGuaranteedImpressions] = useState(
  editing?.guaranteed_impressions != null ? String(editing.guaranteed_impressions) : ''
 );
 const [industry, setIndustry] = useState(editing?.industry_ksic_major ?? '');
 const [sido, setSido] = useState(editing?.region_sido ?? '');
 const [gugun, setGugun] = useState(editing?.region_gugun ?? '');
 const [industries, setIndustries] = useState<{ code: string; ko_name: string; group_name?: string }[]>([]);
 const [regions, setRegions] = useState<{ sido: string; gugun: string }[]>([]);
 const [slotVideos, setSlotVideos] = useState<SlotVideo[]>([]);
 const [error, setError] = useState<string | null>(null);
 const [submitting, setSubmitting] = useState(false);

 useEffect(() => {
  fetch('/market/listings/meta/industries').then(r => r.json()).then(j => {
   const list: { code: string; ko_name: string; group_name?: string }[] = j.data || [];
   setIndustries(list);
   // legacy KSIC 코드 정리 (수정 모드에서만 의미 있음)
   if (industry && !list.some(i => i.code === industry)) {
    setIndustry('');
   }
  });
  fetch('/market/listings/meta/regions').then(r => r.json()).then(j => setRegions(j.data || []));
  fetch(`/market/listings/family/${moFamilyHash}/slot-videos`, { credentials: 'include' })
   .then(r => r.json())
   .then(j => setSlotVideos(j.data || []))
   .catch(() => setSlotVideos([]));
 }, [moFamilyHash]);

 const sidos = [...new Set(regions.map(r => r.sido))];
 const guguns = sido ? regions.filter(r => r.sido === sido).map(r => r.gugun) : [];

 const submit = async () => {
  if (!title.trim() || !periodStart || !periodEnd) {
   setError('제목/기간은 필수입니다.');
   return;
  }
  setSubmitting(true);
  setError(null);
  try {
   const url = editing ? `/market/listings/${editing.listing_hash}` : '/market/listings';
   const method = editing ? 'PATCH' : 'POST';
   const body: any = {
    side, title, description,
    period_start: periodStart, period_end: periodEnd,
    price_per_impression: side === 'seeking_member' ? 0 : Number(price),
    industry_ksic_major: industry || null,
    region_sido: sido || null,
    region_gugun: gugun || null,
    daily_budget_cap: side !== 'seeking_member' && dailyBudgetCap ? Number(dailyBudgetCap) : null,
    guaranteed_impressions: side !== 'seeking_member' && guaranteedImpressions ? Number(guaranteedImpressions) : null,
   };
   if (!editing) body.mo_family_hash = moFamilyHash;
   const res = await fetch(url, {
    method,
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
   });
   const j = await res.json();
   if (!res.ok || !j.success) throw new Error(j.error || '등록 실패');
   onCreated?.();
   onClose();
  } catch (err: any) {
   setError(err.message || '등록 실패');
  } finally {
   setSubmitting(false);
  }
 };

 return (
  <div
   className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
   onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
  >
   <div className="bg-white p-3 md:p-6 max-w-xl w-full max-h-[90vh] overflow-y-auto">
    <div className="flex justify-between items-center mb-3">
     <h2 className="text-lg md:text-xl font-bold">{editing ? '마켓 등록 수정' : '마켓 등록'}</h2>
     <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
    </div>

    <div className="space-y-3 text-sm">
     <div>
      <label className="block text-gray-600 mb-1">유형</label>
      <div className="flex flex-wrap gap-2">
       <button
        onClick={() => setSide('seeking_advertiser')}
        className={`px-3 py-1 border ${side === 'seeking_advertiser' ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}`}
       >광고 받기 (스크린)</button>
       <button
        onClick={() => setSide('seeking_media')}
        className={`px-3 py-1 border ${side === 'seeking_media' ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}`}
       >미디어 찾기 (광고주)</button>
       <button
        onClick={() => setSide('seeking_member')}
        className={`px-3 py-1 border ${side === 'seeking_member' ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}`}
       >구성원 모집</button>
      </div>
     </div>

     {/* 광고주 모드(seeking_media)일 때만 — 슬롯 점유 영상 = 광고 카탈로그 미리보기.
         미디어사가 listing 보면 이 영상들 중 하나를 골라 제안한다. */}
     {side === 'seeking_media' && (
      <div className="bg-gray-50 border border-gray-200 p-2">
       <div className="text-xs text-gray-600 mb-1">
        노출될 광고 카탈로그 ({slotVideos.length}개)
       </div>
       {slotVideos.length === 0 ? (
        <p className="text-xs text-gray-500">
         활성 슬롯 세트에 영상이 없습니다. 슬롯에 영상을 먼저 할당하세요.
        </p>
       ) : (
        <div className="flex flex-wrap gap-2">
         {slotVideos.map(v => (
          <div key={v.file_hash} className="flex flex-col items-center" title={v.filename}>
           <div className="w-16 h-9 border border-gray-300 overflow-hidden">
            {v.thumbnail_path ? (
             <img src={`/mofile/thumbnail/${v.thumbnail_path}`} alt={v.filename}
              className="w-full h-full object-cover" />
            ) : (
             <div className="w-full h-full bg-gray-200" />
            )}
           </div>
           <span className="text-[10px] truncate w-16 mt-0.5">{v.filename}</span>
          </div>
         ))}
        </div>
       )}
      </div>
     )}

     <div>
      <label className="block text-gray-600 mb-1">제목 *</label>
      <input
       type="text"
       value={title}
       onChange={e => setTitle(e.target.value)}
       maxLength={200}
       className="w-full px-2 py-1 border border-gray-300"
      />
     </div>

     <div>
      <label className="block text-gray-600 mb-1">설명</label>
      <textarea
       value={description}
       onChange={e => setDescription(e.target.value)}
       rows={3}
       className="w-full px-2 py-1 border border-gray-300"
      />
     </div>

     <div className="grid grid-cols-2 gap-2">
      <div>
       <label className="block text-gray-600 mb-1">기간 시작 *</label>
       <input type="date" value={periodStart} onChange={e => setPeriodStart(e.target.value)} className="w-full px-2 py-1 border border-gray-300" />
      </div>
      <div>
       <label className="block text-gray-600 mb-1">기간 종료 *</label>
       <input type="date" value={periodEnd} onChange={e => setPeriodEnd(e.target.value)} className="w-full px-2 py-1 border border-gray-300" />
      </div>
     </div>

     {side !== 'seeking_member' && (
      <>
       <div>
        <label className="block text-gray-600 mb-1">
         단가 (원/회) *{' '}
         <span className="text-gray-400 cursor-help" title={priceTooltipText(Number(price))}>ⓘ</span>
        </label>
        <input
         type="number" step="0.1" min="0"
         value={price} onChange={e => setPrice(e.target.value)}
         className="w-32 px-2 py-1 border border-gray-300"
        />
        <div className="mt-1">
         <PriceCostBasisHint moFamilyHash={moFamilyHash} pricePerImpression={Number(price)} />
        </div>
       </div>
       <div className="grid grid-cols-2 gap-2">
        <div>
         <label className="block text-gray-600 mb-1">
          하루 최대 책정 금액 (원){' '}
          <span
           className="text-gray-400 cursor-help"
           title={'광고주 안전 장치 — 그 날 송출이 캡을 넘어도 청구는 캡까지만.\n송출은 계속됩니다 (잉여분은 미디어사 무상 노출).'}
          >ⓘ</span>
         </label>
         <input
          type="number" step="100" min="0"
          value={dailyBudgetCap} onChange={e => setDailyBudgetCap(e.target.value)}
          placeholder="(미지정 = 한도 없음)"
          className="w-full px-2 py-1 border border-gray-300"
         />
         <p className="text-[10px] text-gray-500 mt-0.5">캡까지만 청구 (송출은 계속)</p>
        </div>
        <div>
         <label className="block text-gray-600 mb-1">
          송출 횟수 보장 (전 기간){' '}
          <span
           className="text-gray-400 cursor-help"
           title={'기간 종료 시점 보장치 대비:\n· 50% 미만 송출 → 전액 환불\n· 50~100% 구간 → 실제 송출 비율로 비례 청구\n· 100% 이상 → 보장치 × 단가 정액 청구'}
          >ⓘ</span>
         </label>
         <input
          type="number" step="100" min="0"
          value={guaranteedImpressions} onChange={e => setGuaranteedImpressions(e.target.value)}
          placeholder="(미지정 = 보장 없음)"
          className="w-full px-2 py-1 border border-gray-300"
         />
         <p className="text-[10px] text-gray-500 mt-0.5">50% 미만 = 전액 환불 / 50~100% = 비례</p>
        </div>
       </div>
      </>
     )}

     <div>
      <label className="block text-gray-600 mb-1">
       매장 분류{' '}
       <span
        className="text-gray-400 cursor-help"
        title={'스크린이 설치된 장소의 업종 (또는 광고주의 사업 분류). 광고주·미디어 매칭 정확도를 높이기 위해 대분류로 선택.'}
       >ⓘ</span>
      </label>
      <select value={industry} onChange={e => setIndustry(e.target.value)} className="w-full px-2 py-1 border border-gray-300">
       <option value="">선택 안 함</option>
       {Object.entries(industries.reduce((acc, i) => {
        const g = i.group_name || '기타';
        (acc[g] ||= []).push(i);
        return acc;
       }, {} as Record<string, typeof industries>)).map(([group, items]) => (
        <optgroup key={group} label={group}>
         {items.map(i => <option key={i.code} value={i.code}>{i.ko_name}</option>)}
        </optgroup>
       ))}
      </select>
     </div>

     <div className="grid grid-cols-2 gap-2">
      <div>
       <label className="block text-gray-600 mb-1">시·도</label>
       <select value={sido} onChange={e => { setSido(e.target.value); setGugun(''); }} className="w-full px-2 py-1 border border-gray-300">
        <option value="">선택 안 함</option>
        {sidos.map(s => <option key={s} value={s}>{s}</option>)}
       </select>
      </div>
      <div>
       <label className="block text-gray-600 mb-1">구·군</label>
       <select value={gugun} onChange={e => setGugun(e.target.value)} disabled={!sido} className="w-full px-2 py-1 border border-gray-300 disabled:opacity-50">
        <option value="">선택 안 함</option>
        {guguns.map(g => <option key={g} value={g}>{g}</option>)}
       </select>
      </div>
     </div>

     {error && <div className="bg-red-50 text-red-700 p-2 text-xs">{error}</div>}

     <div className="flex gap-2 pt-2">
      <button
       onClick={submit}
       disabled={submitting}
       className="flex-1 px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white disabled:opacity-50"
      >
       {submitting ? (editing ? '저장 중...' : '등록 중...') : (editing ? '저장' : '등록')}
      </button>
      <button
       onClick={onClose}
       className="flex-1 px-3 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700"
      >
       취소
      </button>
     </div>
    </div>
   </div>
  </div>
 );
};

export default MarketRegisterModal;
